import React from 'react';
import PropTypes from 'prop-types';
import { FormRow, Tooltip, Select, Loader } from '@here/ref-client-ui';
import './selectsFormView.scss';
import stringParseUrl from '../stringParseUrl';
import set from 'lodash/set';
import get from 'lodash/get';
import { getOpenapiInfo, getOpenapiUrl } from '../openapiHelpers';
import yamlLoad from '../checkboxes/yamlLoad';
import Searchable from '../../../views/shared/searchUtils/Searchable';

class SelectFormView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      openapiInfo: null,
      params: [],
      isLoading: false,
    };
  }
  componentDidMount() {
    const {
      options: { openapiInfo },
      settings,
      fields: { customizations }
    } = this.props;

    if (openapiInfo) {
      const { urlPath, urlRegex, urlSuffix, keys } = openapiInfo;
      const propsUrl = get(this.props, urlPath);
      const url = getOpenapiUrl(propsUrl, urlRegex, urlSuffix);
      getOpenapiInfo(url, keys, customizations, settings).then((openapiInfo) => {
        if (!this.isUnmounted) {
          this.setState({ openapiInfo });
        }
      });
    }
    this.loadParams();
  }

  componentDidUpdate(prevProps) {
    const {
      options: { loadParams },
    } = this.props;
    if (loadParams) {
      const { urlPath } = loadParams;
      const customizations = get(this.props, 'fields.customizations');

      if (get(this.props, urlPath) !== get(prevProps, urlPath) 
        || customizations !== get(prevProps, 'fields.customizations')
        || (customizations && get(this.props, 'settings.apiKey') !== get(prevProps, 'settings.apiKey'))
        || (customizations && get(this.props, 'settings.token') !== get(prevProps, 'settings.token'))
      ) {
        this.loadParams();
      }
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  loadParams = () => {
    const {
      options: { loadParams },
    } = this.props;
    if (loadParams) {
      const { url, resultPath, urlPath, urlRegex, urlSuffix } = loadParams;

      let openapiUrl;
      if (url) {
        openapiUrl = url;
      } else {
        const propsUrl = get(this.props, urlPath, '');
        const regexp = new RegExp(urlRegex);
        const matchedUrl = propsUrl.match(regexp);
        if (matchedUrl) {
          openapiUrl = `${matchedUrl[0]}${urlSuffix}`;
        }
      }
      if (!openapiUrl) {
        return;
      }
      this.setState({ isLoading: true, params: [] });
      const { settings, fields: { customizations } } = this.props;
      yamlLoad(openapiUrl, customizations, settings).then((data) => {
        if (this.isUnmounted) {
          return;
        }
        let params = get(data, resultPath).filter(
          (value) => !value.startsWith('-')
        );
        params = params.map((value) => ({ value, label: value }));
        this.setState({ isLoading: false, params });
      });
    }
  };

  getTooltip() {
    const {
      options: { tooltip },
    } = this.props;
    let tooltipEl = null;
    const { openapiInfo } = this.state;
    if (tooltip || openapiInfo) {
      tooltipEl = (
        <div className="rf-form-view__tooltip">{tooltip || openapiInfo}</div>
      );
    }

    return tooltipEl;
  }

  onChange = (v, e) => {
    const {
      options: { key, delimiter },
      setFields,
      fields
    } = this.props;
    const value = fields[key];
    const [firstv='', secondv = ''] = value.split(delimiter);

    if (v === 0)
      setFields({ [key]: secondv ? (e.target.value + delimiter + secondv) : e.target.value });
    else if (v === 1)
      setFields({ [key]: e.target.value ? (firstv + delimiter + e.target.value) : firstv });

  };

  render() {
    const {
      options: { label, selectOptions, key, delimiter, loadParams, initValue = '', noEmptyOption },
      fields,
    } = this.props;
    const { params: stateParams, isLoading } = this.state;
    const value = fields[key];
    const [firstv='', secondv = ''] = value.split(delimiter);
    const [firstvInit='', secondvInit = ''] = initValue.split(delimiter);
    
    const getSelectEl = (options) => (
      <>
      <Select
        label={label}
        options={options[0]}
        value={firstv||firstvInit}
        onChange={this.onChange.bind(this, 0)}
        tooltip={this.getTooltip()}
        tooltipPlacement="right"
        addEmptyOption={!noEmptyOption}
      />
      <Select
        options={options[1]}
        value={secondv||secondvInit}
        onChange={this.onChange.bind(this, 1)}
        tooltip={this.getTooltip()}
        addEmptyOption={!noEmptyOption}
      />
      </>
    );

    const params = loadParams ? stateParams : selectOptions;
    const selectedOption1 = params[0].find(
      (option) => option.value === firstv
    );
    const selectedOption2 = params[1].find(
      (option) => option.value === secondv
    );
    
    if ((selectedOption1 || !firstv) && (selectedOption2 || !secondv)) {
      return (
        <Searchable searchKey={key}>
          <FormRow className={'rf-multi-selects'}>{getSelectEl(params)}</FormRow>
        </Searchable>
      );
    }
    
    const extendedParams1 = (!selectedOption1 && firstv) ? [...params[0], {value: firstv, label: firstv}] : params[0];
    const extendedParams2 = (!selectedOption2 && secondv) ? [...params[1], {value: secondv, label: secondv}] : params[1];

    const tooltip = `Select option "${fields[key]}" is not known by current configuration.`;
    return (
      <FormRow className="rf-select-form-view__unknown-select rf-multi-selects-tooltip">
        <Tooltip tooltip={tooltip}>
          <div className="rf-multi-selects-tooltip-container">
            {getSelectEl([
              extendedParams1,
              extendedParams2
            ])}
          </div>
        </Tooltip>
        {isLoading && <Loader />}
      </FormRow>
    );
  }
}

SelectFormView.propTypes = {
  fields: PropTypes.object,
  options: PropTypes.object,
  setFields: PropTypes.func.isRequired,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  parseUrl: stringParseUrl,
  defaultState: (options) => ({
    [options.key]: options.initValue || '',
  }),
  getRequestOptions: (fields, options) => {
    const { key, isPost, type } = options;
    if (!fields[key]) return {};
    if (isPost) {
      const data = {};
      let value = fields[key];
      if (type === 'boolean') {
        if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        }
      }
      set(data, key, value);
      return {
        method: 'post',
        data,
      };
    }
    const params = { [key]: fields[key] };
    return { params };
  },
  Component: SelectFormView,
};
